import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

// Cache for download URLs to reduce function calls
const urlCache = new Map();
const CACHE_DURATION = 518400000; // 6 days in milliseconds (URLs expire in 7 days, cache for 6)

// File upload with progress tracking (R2 version)
export const uploadFileWithProgress = (file, path, onProgress, onError, onComplete) => {
  const xhr = new XMLHttpRequest();
  
  // Get upload URL from Firebase Function
  const getUploadUrl = httpsCallable(functions, 'getR2UploadUrl');
  
  getUploadUrl({
    fileName: file.name,
    contentType: file.type,
    folder: path.split('/')[0] || 'uploads'
  })
    .then(({ data }) => {
      if (!data.success) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, fileKey, fileUrl } = data;

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100;
          onProgress?.(progress, { bytesTransferred: e.loaded, totalBytes: e.total });
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          try {
            // Save metadata to Firestore
            const saveMetadata = httpsCallable(functions, 'saveFileMetadata');
            await saveMetadata({
              fileKey,
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              folder: path.split('/')[0] || 'uploads',
            });

            onComplete?.(fileUrl, {
              fullPath: fileKey,
              name: file.name,
              size: file.size,
              contentType: file.type
            });
          } catch (error) {
            onError?.(error);
          }
        } else {
          onError?.(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        onError?.(new Error('Upload failed'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    })
    .catch((error) => {
      onError?.(error);
    });

  return {
    cancel: () => xhr.abort(),
    snapshot: { ref: { fullPath: path } }
  };
};

// Simple file upload (R2 version)
export const uploadFile = async (file, path) => {
  try {
    const getUploadUrl = httpsCallable(functions, 'getR2UploadUrl');
    const { data } = await getUploadUrl({
      fileName: file.name,
      contentType: file.type,
      folder: path.split('/')[0] || 'uploads'
    });

    if (!data.success) {
      throw new Error('Failed to get upload URL');
    }

    const { uploadUrl, fileKey, fileUrl } = data;

    // Upload to R2
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    // Save metadata to Firestore
    const saveMetadata = httpsCallable(functions, 'saveFileMetadata');
    await saveMetadata({
      fileKey,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      folder: path.split('/')[0] || 'uploads',
    });

    return {
      success: true,
      url: fileUrl, // Keep the direct URL for now
      fileKey: fileKey, // Store fileKey for generating signed URLs later
      metadata: {
        fullPath: fileKey,
        name: file.name,
        size: file.size,
        contentType: file.type
      },
      path: fileKey
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// Upload text content (R2 version)
export const uploadText = async (content, path, contentType = 'text/plain') => {
  try {
    const blob = new Blob([content], { type: contentType });
    const file = new File([blob], path.split('/').pop() || 'file.txt', { type: contentType });
    
    return await uploadFile(file, path);
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete file (R2 version)
export const deleteFile = async (path) => {
  try {
    const deleteFileFunc = httpsCallable(functions, 'deleteR2File');
    const { data } = await deleteFileFunc({ fileKey: path });

    if (!data.success) {
      throw new Error('Failed to delete file');
    }

    // Clear from cache
    urlCache.delete(path);

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// Get file metadata (R2 version - from Firestore)
export const getFileMetadata = async (path) => {
  try {
    // In R2 version, metadata is stored in Firestore
    // This would need a Firebase Function to retrieve it
    return {
      success: true,
      metadata: {
        fullPath: path,
        // Other metadata would come from Firestore
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// Update file metadata (R2 version)
export const updateFileMetadata = async (path, metadata) => {
  try {
    // Metadata updates would be handled in Firestore
    return {
      success: true,
      metadata
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// List files in a directory (R2 version - from Firestore)
export const listFiles = async (path, maxResults = 1000) => {
  try {
    // This would need a Firebase Function to list files from Firestore
    // For now, return empty
    return {
      success: true,
      files: [],
      prefixes: []
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// Get download URL (R2 version)
export const getFileDownloadURL = async (path, expiresIn = 604800) => {
  try {
    // Check cache first
    const cached = urlCache.get(path);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return {
        success: true,
        url: cached.url
      };
    }

    const getDownloadUrl = httpsCallable(functions, 'getR2DownloadUrl');
    const { data } = await getDownloadUrl({ fileKey: path, expiresIn });

    if (!data.success) {
      throw new Error('Failed to get download URL');
    }

    // Cache the URL with metadata
    urlCache.set(path, {
      url: data.downloadUrl,
      timestamp: Date.now(),
      expiresIn: data.expiresIn || expiresIn,
      generatedAt: data.generatedAt || Date.now()
    });

    return {
      success: true,
      url: data.downloadUrl
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// Upload multiple files
export const uploadMultipleFiles = async (files, basePath, onProgress) => {
  const results = [];
  const totalFiles = files.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileName = `${Date.now()}_${file.name}`;
    const filePath = `${basePath}/${fileName}`;

    try {
      const result = await uploadFile(file, filePath);
      results.push({
        ...result,
        originalFile: file,
        index: i
      });

      if (onProgress) {
        onProgress((i + 1) / totalFiles * 100, i + 1, totalFiles);
      }
    } catch (error) {
      results.push({
        success: false,
        error: error.message,
        originalFile: file,
        index: i
      });
    }
  }

  return results;
};

// Generate unique file path
export const generateFilePath = (fileName, folder = 'uploads', userId = null) => {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 15);
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
  
  if (userId) {
    return `${folder}/${userId}/${timestamp}_${randomId}_${sanitizedFileName}`;
  }
  
  return `${folder}/${timestamp}_${randomId}_${sanitizedFileName}`;
};

// Validate file before upload
export const validateFile = (file, options = {}) => {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB
    allowedTypes = ['image/*', 'video/*', 'application/pdf', 'text/*', 'application/*'],
    maxFileNameLength = 255
  } = options;

  const errors = [];

  if (file.size > maxSize) {
    errors.push(`File size exceeds ${formatFileSize(maxSize)} limit`);
  }

  if (file.name.length > maxFileNameLength) {
    errors.push(`File name is too long (max ${maxFileNameLength} characters)`);
  }

  const isAllowedType = allowedTypes.some(type => {
    if (type.endsWith('/*')) {
      return file.type.startsWith(type.slice(0, -1));
    }
    return file.type === type;
  });

  if (!isAllowedType) {
    errors.push(`File type ${file.type} is not allowed`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get file extension
export const getFileExtension = (fileName) => {
  return fileName.slice((fileName.lastIndexOf('.') - 1 >>> 0) + 2);
};

// Get file type category
export const getFileTypeCategory = (fileType) => {
  if (fileType.startsWith('image/')) return 'image';
  if (fileType.startsWith('video/')) return 'video';
  if (fileType.startsWith('audio/')) return 'audio';
  if (fileType === 'application/pdf') return 'document';
  if (fileType.startsWith('text/')) return 'text';
  if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('tar')) return 'archive';
  return 'other';
};

// Create thumbnail for images
export const createImageThumbnail = (file, maxWidth = 200, maxHeight = 200) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const { width, height } = img;
      let newWidth = width;
      let newHeight = height;

      if (width > maxWidth) {
        newWidth = maxWidth;
        newHeight = (height * maxWidth) / width;
      }

      if (newHeight > maxHeight) {
        newHeight = maxHeight;
        newWidth = (newWidth * maxHeight) / newHeight;
      }

      canvas.width = newWidth;
      canvas.height = newHeight;

      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob((blob) => {
        resolve(new File([blob], `thumb_${file.name}`, { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.8);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};

// Batch operations
export const batchUploadFiles = async (files, basePath, options = {}) => {
  const {
    onProgress,
    onFileComplete,
    onError,
    validateEach = true,
    createThumbnails = false
  } = options;

  const results = [];
  const totalFiles = files.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    try {
      if (validateEach) {
        const validation = validateFile(file);
        if (!validation.isValid) {
          throw new Error(validation.errors.join(', '));
        }
      }

      let thumbnailResult = null;
      if (createThumbnails && file.type.startsWith('image/')) {
        try {
          const thumbnail = await createImageThumbnail(file);
          const thumbnailPath = `${basePath}/thumbnails/${generateFilePath(thumbnail.name)}`;
          thumbnailResult = await uploadFile(thumbnail, thumbnailPath);
        } catch (error) {
          // Thumbnail creation failed, continue with main upload
        }
      }

      const filePath = generateFilePath(file.name, basePath);
      const result = await uploadFile(file, filePath);

      const fileResult = {
        ...result,
        originalFile: file,
        index: i,
        thumbnail: thumbnailResult
      };

      results.push(fileResult);
      onFileComplete?.(fileResult, i + 1, totalFiles);

    } catch (error) {
      const errorResult = {
        success: false,
        error: error.message,
        originalFile: file,
        index: i
      };

      results.push(errorResult);
      onError?.(errorResult, i + 1, totalFiles);
    }

    if (onProgress) {
      onProgress((i + 1) / totalFiles * 100, i + 1, totalFiles);
    }
  }

  return results;
};

// Storage quota management
export const getStorageUsage = async (userId = null) => {
  try {
    // This would need a Firebase Function to calculate from Firestore
    return {
      success: true,
      totalSize: 0,
      fileCount: 0,
      formattedSize: '0 Bytes'
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

export default {
  uploadFileWithProgress,
  uploadFile,
  uploadText,
  deleteFile,
  getFileMetadata,
  updateFileMetadata,
  listFiles,
  getFileDownloadURL,
  uploadMultipleFiles,
  generateFilePath,
  validateFile,
  formatFileSize,
  getFileExtension,
  getFileTypeCategory,
  createImageThumbnail,
  batchUploadFiles,
  getStorageUsage
};
